

import React, { useEffect, useRef } from "react";
import * as THREE from "three";

const ParticleFooter = () => {
  const containerRef = useRef(null);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;

    const container = containerRef.current;
    if (!container) return;

    let width = container.offsetWidth || window.innerWidth;
    let height = container.offsetHeight || 300;
    if (width === 0) width = window.innerWidth;
    if (height === 0) height = 300;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 1, 1000);
    camera.position.z = 120;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    const getVisibleSize = () => {
      const vFOV = (camera.fov * Math.PI) / 180;
      const visibleHeight = 2 * Math.tan(vFOV / 2) * camera.position.z;
      const visibleWidth = visibleHeight * camera.aspect;
      return { visibleWidth, visibleHeight };
    };

    let { visibleWidth, visibleHeight } = getVisibleSize();

    const PARTICLES = width < 640 ? 6000 : width < 1024 ? 10000 : 15000;

    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(PARTICLES * 3);
    const original = new Float32Array(PARTICLES * 3);
    const randoms = new Float32Array(PARTICLES);
    // Per-particle phase offsets so each particle moves independently
    const phaseX = new Float32Array(PARTICLES);
    const phaseY = new Float32Array(PARTICLES);
    const phaseZ = new Float32Array(PARTICLES);
    // Per-particle speed multiplier for varied drift
    const speeds = new Float32Array(PARTICLES);

    for (let i = 0; i < PARTICLES; i++) {
      const i3 = i * 3;
      const x = (Math.random() - 0.5) * visibleWidth;
      const y = (Math.random() - 0.5) * visibleHeight;
      const z = (Math.random() - 0.5) * 50;

      positions[i3] = x;
      positions[i3 + 1] = y;
      positions[i3 + 2] = z;
      original[i3] = x;
      original[i3 + 1] = y;
      original[i3 + 2] = z;

      randoms[i] = Math.random();
      phaseX[i] = Math.random() * Math.PI * 2;
      phaseY[i] = Math.random() * Math.PI * 2;
      phaseZ[i] = Math.random() * Math.PI * 2;
      // Slower, more varied speeds: 0.3 – 0.7x
      speeds[i] = 0.5 + Math.random() * 0.6;
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color: "#ffffff",
      size: 0.9,           // slightly smaller for a crisper look
      transparent: true,
      opacity: 0.3,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // Smooth mouse
    const mouse = { x: 0, y: 0 };
    const targetMouse = { x: 0, y: 0 };
    let mouseActive = false;

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      targetMouse.x = x * (visibleWidth / 2);
      targetMouse.y = y * (visibleHeight / 2);
      mouseActive = true;
    };

    container.addEventListener("mousemove", handleMouseMove);

    const handleResize = () => {
      width = container.offsetWidth || window.innerWidth;
      height = container.offsetHeight || 300;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
      const size = getVisibleSize();
      visibleWidth = size.visibleWidth;
      visibleHeight = size.visibleHeight;
    };

    window.addEventListener("resize", handleResize);

    let animationFrameId;
    let time = 0;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // Slower time increment = smoother, more graceful motion
      time += 0.008;

      // Slower mouse lerp = smoother follow
      mouse.x += (targetMouse.x - mouse.x) * 0.04;
      mouse.y += (targetMouse.y - mouse.y) * 0.04;

      const pos = geometry.attributes.position.array;

      for (let i = 0; i < PARTICLES; i++) {
        const i3 = i * 3;
        const ox = original[i3];
        const oy = original[i3 + 1];
        const oz = original[i3 + 2];
        const s = speeds[i];

        // Smaller amplitude + per-particle phase = organic, non-uniform drift
        let x = ox
          + Math.sin(time * s + phaseX[i] + ox * 0.015) * 3
          + Math.cos(time * s * 0.7 + phaseY[i] + oy * 0.01) * 1.5;

        let y = oy
          + Math.cos(time * s + phaseY[i] + oy * 0.015) * 3
          + Math.sin(time * s * 0.7 + phaseX[i] + ox * 0.01) * 1.5;

        let z = oz
          + Math.sin(time * s * 0.6 + phaseZ[i]) * 5;

        // Softer mouse attraction: larger radius, gentler force
        if (mouseActive) {
          const dx = mouse.x - x;
          const dy = mouse.y - y;
          const distSq = dx * dx + dy * dy;
          const radius = 100;
          const radiusSq = radius * radius;

          if (distSq < radiusSq && distSq > 0) {
            const dist = Math.sqrt(distSq);
            // Smoother falloff: linear instead of quadratic
            const force = (1 - dist / radius) * 0.5;
            x += (dx / dist) * force * 18;
            y += (dy / dist) * force * 18;
            z += force * 8;
          }
        }

        pos[i3] = x;
        pos[i3 + 1] = y;
        pos[i3 + 2] = z;
      }

      geometry.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
    };

    animate();

    return () => {
      mountedRef.current = false;
      cancelAnimationFrame(animationFrameId);
      container.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      geometry.dispose();
      material.dispose();
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        background: "transparent",
      }}
    />
  );
};

export default ParticleFooter;